import { Typography, AutocompleteRenderInputParams, Box } from '@mui/material';
import { uniqBy } from 'lodash';
import { Item } from '@components/select/Select';
import { Autocomplete } from '@components/autocomplete';
import { TextField } from '@components/textField/TextField';
import * as autocompleteStyle from '@components/autocomplete/Autocomplete.styles';
import { Times, CaretDown } from '@assets/svgs';
import { CollapsedField } from '@components/quoteDetails/CollapsedField';
import { QFCTypeExecuted } from '@components/quoteDetails/QuoteFieldControl';
import { requiredFieldStyle, requiredBeforeBindFieldStyle } from '@components/inputLabel/InputLabel.styles';
import { useMemo, useState } from 'react';
import { AdditionalInfoLink } from '@components/container/AdditionalInfoLink';

export interface FilteredSelectFieldProps extends QFCTypeExecuted {
  onBlur: () => void;
  isRequired: boolean;
  isRequiredBeforeBind?: boolean;
  getOptionValues: ({ isOverriding }: { isOverriding: boolean }) => Item[];
}

export const FilteredSelectField = ({
  setValue,
  attributeType,
  errorMessage,
  value,
  isCollapsed,
  externalIcon,
  isRequired,
  isRequiredBeforeBind,
  isReadOnly,
  onBlur,
  getOptionValues,
  isOverriding,
  additionalInfoLink,
}: FilteredSelectFieldProps) => {
  let options = getOptionValues ? getOptionValues({ isOverriding }) : [];
  const [isDirty, setIsDirty] = useState(false);

  options = uniqBy(options, (v) => v.value);
  options.push({ label: '', value: '' });

  if (
    value !== undefined &&
    value !== null &&
    !options.find((v: { value: string; label: string }) => v.value == value)
  ) {
    const overrideOptions = getOptionValues ? getOptionValues({ isOverriding: true }) : [];

    const overrideOptionValueLabel =
      overrideOptions && overrideOptions?.find((v: { value: string; label: string }) => v.value == value)?.label;

    options.push({
      value: value as string,
      label: `${overrideOptionValueLabel ?? value}`,
      hidden: true,
    });
  }
  const defaultValueLabel = useMemo(() => {
    return options.find((item) => {
      return item.value === value;
    })?.label;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  if (isCollapsed) {
    return (
      <CollapsedField label={attributeType.label} value={defaultValueLabel} deprecated={attributeType.deprecated} />
    );
  }
  // do not show "x" icon for state fields
  const disableClearable =
    isOverriding || !value || ['MailingState', 'BillingState', 'StateCode'].includes(attributeType.codeName);

  return (
    <Autocomplete
      openOnFocus
      disableAutoSelectOnTypeMatch
      onBlur={onBlur}
      noOptionsText=''
      options={options}
      isOptionEqualToValue={(option: any, value: any) => option.value === value}
      fullWidth
      clearIcon={disableClearable ? null : <Times />}
      popupIcon={<CaretDown />}
      value={value ?? ''}
      onInputChange={() => {
        setIsDirty(true);
      }}
      onClose={() => {
        setIsDirty(false);
      }}
      onChange={(event, value, reason) => {
        setIsDirty(true);
        if (reason === 'clear') {
          setValue('');
        } else {
          setValue((value as Item).value ?? null);
        }
      }}
      disabled={isReadOnly}
      renderOption={(props, option) =>
        (option as Item)?.value === '' || (option as Item)?.hidden ? null : (
          <li {...props} key={props.key}>
            <Typography variant='bodySmall'>{(option as Item).label}</Typography>
          </li>
        )
      }
      isQuotePanel
      renderInput={(params: AutocompleteRenderInputParams) => {
        const selectedValueLabel = options.find((item) => {
          return params.inputProps.value === item.label;
        })?.label;
        return (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Typography
                variant='caption'
                component='label'
                aria-label={attributeType.label ?? 'Missing label'}
                sx={[
                  {
                    color: errorMessage ? 'error.main' : isReadOnly ? 'text.disabled' : 'text.main',
                  },
                  isRequired
                    ? { ...autocompleteStyle.quotePanelLabel, ...requiredFieldStyle }
                    : isRequiredBeforeBind
                      ? { ...autocompleteStyle.quotePanelLabel, ...requiredBeforeBindFieldStyle }
                      : { ...autocompleteStyle.quotePanelLabel },
                ]}
              >
                {attributeType.label ?? 'Missing label'}
              </Typography>
            </Box>
            {additionalInfoLink && <AdditionalInfoLink {...additionalInfoLink} />}
            <TextField
              deprecated={attributeType.deprecated}
              error={!!errorMessage}
              helperText={errorMessage ?? attributeType?.helpText ?? ' '}
              required={isRequired}
              requiredBeforeBind={isRequiredBeforeBind}
              externalIcon={externalIcon}
              slotProps={{
                input: {
                  ...params.InputProps,
                },
                htmlInput: {
                  ...params.inputProps,
                  value: !isDirty ? defaultValueLabel : selectedValueLabel,
                },
              }}
              {...params}
            />
          </>
        );
      }}
      slotProps={{
        listbox: {
          style: {
            maxHeight: '300px',
          },
        },
      }}
    />
  );
};
